import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import type { ChecklistDetail } from '../../shared/checklistUtils';
import { formatContextDate } from '../../shared/format';
import type {
  SurchargeCheckRowView,
  SurchargeFestiveDateView,
  SurchargePeriodRowView,
} from '../../tabs/surcharges/types';
import type { WithChecklistItemMetadata } from '../../types';
import { presentRowBase } from '../rowBase';

const DEFAULT_SURCHARGE_CURRENCY = 'AUD';
// Blocked rows never ran the coverage check, so their (possibly empty) festive dates would read as a result.
const FESTIVE_BLOCKED_NOTES: Partial<Record<CmChecklist.SurchargeFestiveCoverageReasonCode, string>> = {
  blackout_source_unavailable: 'Blackout dates could not be loaded from Salesforce — festive coverage was not checked.',
  source_unavailable: 'Surcharges could not be loaded — festive coverage was not checked.',
  travel_period_missing: 'Travel period is unknown — festive coverage was not checked.',
};

export interface PresentedSurcharges {
  base: Array<WithChecklistItemMetadata<SurchargeCheckRowView>>;
  table: Array<WithChecklistItemMetadata<SurchargePeriodRowView>>;
}

export function presentSurchargesRows({
  checkRows,
  festiveCoverageRows,
  periodRows,
}: {
  checkRows: Array<CmChecklist.SurchargeCheckRow>;
  festiveCoverageRows: Array<CmChecklist.SurchargeFestiveCoverageRow>;
  periodRows: Array<CmChecklist.SurchargePeriodRow>;
}): PresentedSurcharges {
  return {
    base: [...checkRows.map(presentSurchargeCheckRow), ...festiveCoverageRows.map(presentFestiveCoverageRow)],
    table: periodRows.map(presentSurchargePeriodRow),
  };
}

function presentSurchargeCheckRow(
  row: CmChecklist.SurchargeCheckRow,
): WithChecklistItemMetadata<SurchargeCheckRowView> {
  const base = presentRowBase(row);

  if (row.evidence.check === 'coverage') {
    return {
      ...base,
      details: presentCoverageDetails(row.evidence),
      field: 'Surcharge Coverage',
      name: 'Surcharge coverage rule',
    };
  }

  const overlappingPairs = presentOverlappingPairs(row.evidence, row.subject.surcharges);

  return {
    ...base,
    details: [{ label: 'Overlapping pairs', value: overlappingPairs.join('; ') || 'None' }],
    field: 'Overlapping Surcharge Dates',
    name: 'Overlapping surcharge dates',
    overlappingPairs,
  };
}

function presentFestiveCoverageRow(
  row: CmChecklist.SurchargeFestiveCoverageRow,
): WithChecklistItemMetadata<SurchargeCheckRowView> {
  const base = {
    ...presentRowBase(row),
    field: 'Festive Date Coverage' as const,
    name: 'Festive date coverage',
  };
  const sourceNote = row.status === 'blocked' ? festiveBlockedNote(row.reason_codes) : undefined;

  if (sourceNote) {
    return {
      ...base,
      details: [
        { label: 'Travel from', value: formatContextDate(row.evidence.travel_from ?? undefined) },
        { label: 'Travel to', value: formatContextDate(row.evidence.travel_to ?? undefined) },
        { label: 'Festive coverage', value: sourceNote },
      ],
      sourceNote,
    };
  }

  const festiveDates = presentFestiveDates(row.evidence, row.subject);

  return { ...base, details: presentFestiveDetails(row.evidence, festiveDates), festiveDates };
}

function festiveBlockedNote(reasonCodes: Array<CmChecklist.SurchargeFestiveCoverageReasonCode>) {
  return (
    reasonCodes.map((reasonCode) => FESTIVE_BLOCKED_NOTES[reasonCode]).find(Boolean) ??
    'Festive coverage could not be checked.'
  );
}

function presentFestiveDates(
  evidence: CmChecklist.SurchargeFestiveCoverageEvidence,
  subject: CmChecklist.SurchargeFestiveCoverageSubject,
): Array<SurchargeFestiveDateView> {
  const surchargeNames = new Map(
    subject.surcharges.map((entry) => [entry.surcharge_id, entry.name ?? entry.surcharge_id]),
  );
  const blackoutNames = new Map(subject.blackouts.map((entry) => [entry.blackout_id, entry.name ?? entry.blackout_id]));
  const uncovered = new Set(evidence.uncovered_dates);

  return evidence.festive_dates.map((festiveDate) => {
    const covered = !uncovered.has(festiveDate.date);

    return {
      coverage: formatFestiveCoverage({
        blackoutNames: festiveDate.blackout_ids.map((id) => blackoutNames.get(id) ?? id),
        covered,
        surchargeNames: festiveDate.surcharge_ids.map((id) => surchargeNames.get(id) ?? id),
      }),
      covered,
      date: festiveDate.date,
    };
  });
}

function presentFestiveDetails(
  evidence: CmChecklist.SurchargeFestiveCoverageEvidence,
  festiveDates: Array<SurchargeFestiveDateView>,
): Array<ChecklistDetail> {
  if (festiveDates.length === 0) {
    return [
      { label: 'Travel from', value: formatContextDate(evidence.travel_from ?? undefined) },
      { label: 'Travel to', value: formatContextDate(evidence.travel_to ?? undefined) },
      { label: 'Festive dates', value: 'None inside the travel period' },
    ];
  }

  return festiveDates.map((festiveDate) => ({
    label: formatContextDate(festiveDate.date),
    value: festiveDate.coverage,
  }));
}

function formatFestiveCoverage({
  blackoutNames,
  covered,
  surchargeNames,
}: {
  blackoutNames: Array<string>;
  covered: boolean;
  surchargeNames: Array<string>;
}) {
  if (!covered) {
    return 'Not covered — no surcharge or blackout';
  }

  return [
    surchargeNames.length > 0 ? `Surcharge: ${surchargeNames.join(', ')}` : '',
    blackoutNames.length > 0 ? `Blackout: ${blackoutNames.join(', ')}` : '',
  ]
    .filter(Boolean)
    .join('; ');
}

function presentCoverageDetails(evidence: CmChecklist.SurchargeCoverageEvidence): Array<ChecklistDetail> {
  const hasTravelPeriod = evidence.travel_days !== null;

  return [
    { label: 'Travel period days', value: hasTravelPeriod ? String(evidence.travel_days) : 'N/A' },
    { label: 'Surcharge days covered', value: hasTravelPeriod ? String(evidence.surcharge_days ?? 0) : 'N/A' },
    {
      label: 'Coverage',
      value: evidence.coverage_percent != null ? `${evidence.coverage_percent.toFixed(1)}%` : 'N/A',
    },
    { label: 'Travel from', value: formatContextDate(evidence.travel_from ?? undefined) },
    { label: 'Travel to', value: formatContextDate(evidence.travel_to ?? undefined) },
  ];
}

function presentOverlappingPairs(
  evidence: CmChecklist.SurchargeOverlapEvidence,
  surcharges: CmChecklist.SurchargeCheckRow['subject']['surcharges'],
) {
  const namesById = new Map(
    surcharges.map((surcharge) => [surcharge.surcharge_id, surcharge.name ?? surcharge.surcharge_id]),
  );

  return evidence.overlapping_pairs.map(
    ([first, second]) => `${namesById.get(first) ?? first} & ${namesById.get(second) ?? second}`,
  );
}

function presentSurchargePeriodRow(
  row: CmChecklist.SurchargePeriodRow,
): WithChecklistItemMetadata<SurchargePeriodRowView> {
  const base = presentRowBase(row);

  return {
    ...base,
    accumulate: row.evidence.accumulate,
    blackoutOverlap: row.evidence.blackout_overlap,
    blackoutOverlaps: row.evidence.blackout_overlaps.map((overlap, index) => ({
      from__c: overlap.from ?? '',
      name: row.subject.blackout_names[index] ?? '',
      to__c: overlap.to ?? '',
    })),
    convertedCost: row.subject.converted_cost ?? undefined,
    currencyCode: row.subject.currency_code || DEFAULT_SURCHARGE_CURRENCY,
    dealOptionNames: row.subject.deal_option_names,
    fromDate: row.evidence.from_date ?? '',
    insideTravelPeriod: row.evidence.inside_travel_period,
    name: row.subject.name ?? '',
    surchargePrice: row.subject.surcharge_price ?? undefined,
    toDate: row.evidence.to_date ?? '',
  };
}
